import { Stack, Typography } from '@mui/material';
import { webColors } from '@liminal/brand/semantic';
import { WarningCard } from '@liminal/web-ui/feedback/WarningCard';
import { Icon } from '@liminal/web-ui/icons/Icon';
import { Button } from '@liminal/web-ui/primitives/Button';
import { Card } from '@liminal/web-ui/primitives/Card';
import { StatusPill } from '@liminal/web-ui/primitives/StatusPill';
import type { EventGuest } from '@/api/types';
import { eventGuestEligibility } from '@/components/ui/statusHelpers';
import { guestCardView, type IssueOutcome } from '../checkIn';

export interface GuestCardProps {
  readonly guest: EventGuest;
  /** What the last tap of "Issue" came to for this guest, or null before one. */
  readonly outcome: IssueOutcome | null;
  /** The time the card's times are read against, as epoch milliseconds. */
  readonly now: number;
  /** While a tap of "Issue" is in flight. */
  readonly issuing: boolean;
  readonly onIssue: () => void;
}

/**
 * A guest as the door sees them: name, @tag, whether their verification lets
 * them be handed tickets, and the one thing to do about it. Built for a phone
 * held at a bar: one column, the button full width and tall.
 */
export function GuestCard({ guest, outcome, now, issuing, onIssue }: GuestCardProps) {
  const eligibility = eventGuestEligibility(guest.eligibility);
  const view = guestCardView(guest, outcome, now);

  return (
    <Card variant="elevated" padding="lg" data-testid="guest-card">
      <Stack spacing={2}>
        <Stack direction="row" spacing={2} justifyContent="space-between" alignItems="flex-start">
          <Stack spacing={0.5} sx={{ minWidth: 0 }}>
            <Typography variant="h5" sx={{ overflowWrap: 'anywhere' }}>
              {guest.displayName || 'No name on their profile'}
            </Typography>
            {guest.displayTag ? (
              <Typography variant="body1" color="text.secondary" sx={{ overflowWrap: 'anywhere' }}>
                @{guest.displayTag}
              </Typography>
            ) : null}
          </Stack>
          <StatusPill label={eligibility.label} tone={eligibility.tone} data-testid="guest-eligibility" />
        </Stack>

        {view.kind === 'issue' ? (
          <Stack spacing={1}>
            {view.problem ? (
              <WarningCard tone={view.problem.tone} data-testid="guest-notice">
                {view.problem.text}
              </WarningCard>
            ) : null}
            <Button size="lg" fullWidth loading={issuing} onClick={onIssue}>
              {view.buttonLabel}
            </Button>
          </Stack>
        ) : null}

        {view.kind === 'issued-now' ? (
          <Stack spacing={0.5} data-testid="guest-notice">
            <Stack direction="row" spacing={1.5} alignItems="center">
              <Icon name="checkmark-circle" size={32} color={webColors.state.success} />
              <Typography variant="h5">{view.text}</Typography>
            </Stack>
            <Typography variant="body2" color="text.secondary">
              {view.detail}
            </Typography>
          </Stack>
        ) : null}

        {view.kind === 'issued-before' && view.repeat ? (
          <WarningCard tone="warning" data-testid="guest-notice">
            {view.text}
          </WarningCard>
        ) : null}

        {view.kind === 'issued-before' && !view.repeat ? (
          <Typography variant="body1" data-testid="guest-issued">
            {view.text}
          </Typography>
        ) : null}

        {view.kind === 'refused' ? (
          <WarningCard tone={view.notice.tone} data-testid="guest-notice">
            {view.notice.text}
          </WarningCard>
        ) : null}
      </Stack>
    </Card>
  );
}
